import {
  getShoppingCard,
  createShoppingCard,
  updateCartShippingAddress,
  addLineItemsToCart,
  updateCartLineItems,
  applyDiscountToCart,
} from '../EcommerceService.js';

const shoppingCartIdKey = 'shoppingCartId';

function flattenCost(cart) {
  if (!cart?.cost) return cart;
  const { cost, ...rest } = cart;
  return {
    ...rest,
    subtotalPrice: cost.subtotalPrice,
    totalPrice: cost.totalPrice,
    totalTax: cost.totalTax,
  };
}

function flattenCartLines(cart) {
  if (!cart?.lines?.edges) {
    return [];
  }

  return cart.lines.edges.map((edge) => {
    const { node } = edge;
    const merch = node.merchandise || {};
    return {
      id: node.id,
      quantity: node.quantity,
      merchandiseId: merch.id,
      title: merch.product.title,
      variant: merch,
      discountAllocations: node.discountAllocations,
      customAttributes: node.attributes,
    };
  });
}

function calculateDiscountApplicationValueAmount(cart) {
  const values = cart.discountAllocations
    .filter((allocation) => allocation.discountApplication)
    .map((allocation) => allocation.discountApplication.value);
  return values.every((value) => value.amount)
    ? values.reduce((sum, value) => sum + Number.parseFloat(value.amount), 0)
    : null;
}

function fillDiscountApplications(flatCart) {
  const discountApplications = [];
  if (flatCart?.discountAllocations?.[0]) {
    discountApplications[0] = {
      ...flatCart.discountAllocations[0].discountApplication,
      title: flatCart.discountCodes[0].code,
      allocatedAmount: flatCart.discountAllocations[0].discountedAmount,
    };
    const discountApplicationValueAmount = calculateDiscountApplicationValueAmount(flatCart);
    if (discountApplicationValueAmount) {
      discountApplications[0].value = { amount: discountApplicationValueAmount };
    }
  }
  return discountApplications;
}

function getLocalShoppingCartId() {
  return localStorage.getItem(shoppingCartIdKey);
}

function removeLocalShoppingCartId(cartId) {
  localStorage.removeItem(shoppingCartIdKey);
}

function setLocalShoppingCartId(cartId) {
  localStorage.setItem(shoppingCartIdKey, cartId);
}

function removeDraftOrderInfoFromLocalStorage() {
  localStorage.removeItem('draftOrderInfo');
}

function getLocalShippingAddress() {
  return localStorage.getItem('shippingAddress');
}

export async function getOrCreateCart({ isFromWebapp, shippingAddress, eventPrivateApiKey, eventPublicApiKey }) {
  const cartId = getLocalShoppingCartId();
  const defaultShippingAddress = getLocalShippingAddress();

  const res = cartId ? await getShoppingCard({ cartId, eventPrivateApiKey, eventPublicApiKey }) : null;
  let cart = res?.status === 'ok' ? res.result : null;
  const cartShippingAddress = isFromWebapp && shippingAddress?.address1 ? shippingAddress : defaultShippingAddress;
  if (!cart) {
    cart = await createAndStoreCart([], cartShippingAddress);
  } else if (isFromWebapp && shippingAddress?.address1) {
    cart = await updateCartShippingAddress({
      cartId: cart.id,
      shippingAddress,
      eventPrivateApiKey,
      eventPublicApiKey,
    });
  }

  const flatCart = flattenCost(cart);
  flatCart.lineItems = flattenCartLines(flatCart);
  flatCart.discountApplications = fillDiscountApplications(flatCart);

  return flatCart;
}

async function createAndStoreCart(lineItems = [], shippingAddress = null) {
  const res = await createShoppingCard({
    lineItems,
    shippingAddress,
    eventPrivateApiKey: localStorage.getItem('eventPrivateApiKey') || '',
    eventPublicApiKey: localStorage.getItem('eventPublicApiKey') || '',
  });
  const newCart = res.status === 'ok' ? res.result : null;
  if (newCart) {
    setLocalShoppingCartId(newCart.id);
    removeDraftOrderInfoFromLocalStorage();
  }
  return newCart;
}

export async function ecomGetCart({ isFromWebapp, shippingAddress, eventPrivateApiKey, eventPublicApiKey }) {
  const cart = await getOrCreateCart({ isFromWebapp, shippingAddress, eventPrivateApiKey, eventPublicApiKey });
  return cart || null;
}

export async function ecomAddLineItems(cartId, lineItems) {
  const lines = (lineItems || []).map((item) => ({
    merchandiseId: item.variantId,
    quantity: item.quantity || 1,
    attributes: item.customAttributes || [],
  }));
  const cart = await addLineItemsToCart({
    cartId,
    lineItems: lines,
    eventPrivateApiKey: localStorage.getItem('eventPrivateApiKey') || '',
    eventPublicApiKey: localStorage.getItem('eventPublicApiKey') || '',
  });
  if (cart.status === 'ok') {
    return cart.result;
  }
  return {};
}

export async function ecomUpdateCartLineItems({ cartId, lineItems, eventPrivateApiKey, eventPublicApiKey }) {
  const res = await updateCartLineItems({ cartId, lineItems, eventPrivateApiKey, eventPublicApiKey });
  const cart = res?.status === 'ok' ? res.result : null;
  removeDraftOrderInfoFromLocalStorage();
  return cart || {};
}

export async function ecomApplyDiscount({ cartId, discountCode, eventPrivateApiKey, eventPublicApiKey }) {
  const res = await applyDiscountToCart({ cartId, discountCode, eventPrivateApiKey, eventPublicApiKey });
  const cart = res?.status === 'ok' ? res.result : null;
  removeDraftOrderInfoFromLocalStorage();
  return cart || {};
}

export async function ecomResetCart() {
  removeDraftOrderInfoFromLocalStorage();
  removeLocalShoppingCartId();
  return createAndStoreCart([]);
}
